import dayjs from 'dayjs'

import * as C from '@shared/constants'
import * as M from '@/store/mutations'
import * as _ from '@/lodash'
import api from '@/api'

const state = {
    data: [],
    channelReadDate: null,
    globalReadDate: null,
}

export const mutations = {
    [M.SET_NOTIFICATIONS] (state, { notifications }) {
        state.data = notifications
    },
    [M.ADD_NOTIFICATIONS] (state, { notifications }) {
        // Handle duplicate notifications, due to pubsub or EBS mistakes.
        const arr = state.data.concat(notifications)
        state.data = _.uniqBy(arr, 'id')
    },
    [M.SET_CHANNEL_NOTIFICATION_READ_DATE] (state, payload) {
        state.channelReadDate = payload?.date || new Date()
    },
    [M.SET_GLOBAL_NOTIFICATION_READ_DATE] (state, payload) {
        state.globalReadDate = payload?.date || new Date()
    },
}

export const actions = {
    async fetchNotifications ({ commit, getters }, payload) {
        const { channelId, userId, isBroadcaster } = getters

        const url = `channels/${channelId}/users/${userId}/notifications`
        const opts = { params: payload }

        const res = await api.get(url, opts)
        const { notifications, channelReadDate, globalReadDate } = res.data

        commit(M.ADD_NOTIFICATIONS, { notifications })
        if (channelReadDate) {
            commit(M.SET_CHANNEL_NOTIFICATION_READ_DATE, { date: new Date(channelReadDate) })
        }
        if (globalReadDate) {
            commit(M.SET_GLOBAL_NOTIFICATION_READ_DATE, { date: new Date(globalReadDate) })
        } else if (isBroadcaster) {
            // falsy readDate means this is the streamers first time loading the ext!
            commit(M.TOGGLE_BROADCASTER_INTRO_TUTORIAL, { show: true })
        }
    },
    async updateNotificationReadDate ({ commit, getters }) {
        const { channelId, userId, numNotifications } = getters
        commit(M.SET_CHANNEL_NOTIFICATION_READ_DATE)
        commit(M.SET_GLOBAL_NOTIFICATION_READ_DATE)

        const url = `channels/${channelId}/users/${userId}/notifications/read`
        const opts = { params: { numNotifications } }
        await api.put(url, null, opts)
    },
    async readNotifications ({ state, rootState, getters, commit, dispatch }) {
        dispatch('updateNotificationReadDate')

        const suggestions = rootState.suggestions.data
        const notifications = state.data

        // If an incoming notification already exists in the suggestion list,
        // mark the existing one as a notification, and ignore the incoming.
        // This is so you keep any existing client side updates.
        const newSuggestions = suggestions.map(sugg => ({
            ...sugg,
            isNotification: notifications.some(d => d.id === sugg.id)
        }))

        // Notifications that are suggestions, should be moved into the suggestion module,
        // so that they can be handled like any other suggestion.
        for (const n of notifications) {
            if (n.object === 'suggestion') {
                if (!newSuggestions.find(s => s.id === n.id)) {
                    newSuggestions.unshift({ ...n, isNotification: true })
                }
            } else {
                // handle other object types, like changelog
                debug('Unhandled notification!', n)
            }
        }

        commit(M.SET_SUGGESTIONS, { suggestions: newSuggestions })
        commit(M.SET_NOTIFICATIONS, { notifications: [] })
    },
    async handlePubSubSuggestion ({ state, getters, commit }, payload) {
        const readDate = state.channelReadDate
        const { suggestion } = payload
        const { status, createdAt, handledByUserId } = suggestion

        // Pending posts remain notifications until handled by an admin.
        if (C.PENDING_STATUS_TYPES.includes(status)) {
            if (!getters.isAdmin) {
                debug('ignore pubsub suggestion b/c isPending and !isAdmin')
                return
            }
        } else if (handledByUserId === C.OAUTH_HANDLER_MOCK_ID) {
            debug('allow oauth handled suggestion')
        } else if (readDate && dayjs(readDate).isSameOrAfter(createdAt)) {
            debug('ignore pubsub suggestion b/c readDate isSameOrAfter.')
            return
        }
        commit(M.ADD_NOTIFICATIONS, { notifications: [suggestion] })
    },
}

const getters = {
    // TODO: 2.0.13 need to introduce a "notificationScope" property, with values
    // "channel" or "global". the scope determines which readDate to check against.
    // Or just check if a notification has a truthy channelId field.
    numNotifications: state => state.data.length,
}

export default {
    state,
    mutations,
    actions,
    getters
}
